<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { XMarkIcon } from '@heroicons/vue/24/solid'
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogPortal,
  useForwardPropsEmits,
} from 'reka-ui'
import { cn } from '@/utils/utils'
import DialogOverlay from './DialogOverlay.vue'

const props = defineProps<
  DialogContentProps & {
    class?: HTMLAttributes['class']
    hideCloseButton?: boolean
    overlayClass?: HTMLAttributes['class']
  }
>()
const emits = defineEmits<DialogContentEmits>()

const delegatedProps = reactiveOmit(props, 'class', 'hideCloseButton', 'overlayClass')

const forwarded = useForwardPropsEmits(delegatedProps, emits)

/**
 * This component's root is <DialogPortal>, which renders no element — so any
 * attribute a caller passes that is not a declared prop (a `data-testid`, an
 * `aria-*`) fell through to the portal and vanished. Turning inheritance off
 * and spreading `$attrs` onto the content node below puts them on the actual
 * dialog box, where the caller meant them to go.
 */
defineOptions({ inheritAttrs: false })
</script>

<template>
  <DialogPortal>
    <DialogOverlay :class="props.overlayClass" />
    <DialogContent
      data-slot="dialog-content"
      v-bind="{ ...forwarded, ...$attrs }"
      :class="
        cn(
          'elevated-surface data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-3xl p-6 duration-200 sm:max-w-lg',
          props.class,
        )"
    >
      <slot />

      <DialogClose
        v-if="!hideCloseButton"
        class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-5 right-5 rounded-md p-0.5 opacity-70 hover:bg-secondary transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      >
        <XMarkIcon />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
